import React, { useEffect, useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import ReviewForm from './ReviewForm';

import {
  Assessment,
  AssessmentDetail as AssessmentDetailType,
  AssessmentsAPI,
} from '@/services/assessmentsAPI';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import LoadingState from '@/components/LoadingState';
import Alert from '@/components/Alert';
import Chip from '@/components/Chip';
import { Title, Heading, Body, Small } from '@/components/Typography';

interface AssessmentDetailProps {
  assessment: Assessment;
  onReviewSuccess: () => void;
}

const AssessmentDetail: React.FC<AssessmentDetailProps> = ({
  assessment,
  onReviewSuccess,
}) => {
  const [detailedAssessment, setDetailedAssessment] =
    useState<AssessmentDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    loadAssessmentDetails();
  }, [assessment.external_id]);

  const loadAssessmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AssessmentsAPI.getAssessment(
        assessment.external_id,
      );
      setDetailedAssessment(response.data);
    } catch (err) {
      setError('Failed to load assessment details');
      console.error('Error loading assessment details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getResultStatusColor = (
    resultStatus: string | null,
  ): 'success' | 'error' | 'warning' | 'primary' => {
    switch (resultStatus) {
      case 'passed':
        return 'success';
      case 'failed':
        return 'error';
      case 'for_review':
        return 'warning';
      default:
        return 'primary';
    }
  };

  if (loading) {
    return <LoadingState text="Loading assessment details..." />;
  }

  if (error || !detailedAssessment) {
    return (
      <Stack vertical gap={16} align="center">
        <Alert
          status="danger"
          icon={faExclamationTriangle}
          description={error || 'Failed to load assessment'}
        />
        <Button
          onClick={loadAssessmentDetails}
          a11yLabel="Retry loading assessment details"
          label="Retry"
        />
      </Stack>
    );
  }

  return (
    <Stack vertical gap={16}>
      <Stack justify="apart" align="center">
        <Title>Assessment Details</Title>
        {!detailedAssessment.reviewed_at && (
          <Button
            onClick={() => setShowReviewForm(!showReviewForm)}
            a11yLabel={showReviewForm ? 'Cancel review' : 'Review assessment'}
            label={showReviewForm ? 'Cancel Review' : 'Review Assessment'}
          />
        )}
      </Stack>

      <hr
        style={{
          border: 'none',
          borderTop: '1px solid #e0e0e0',
          margin: '8px 0',
        }}
      />

      {showReviewForm ? (
        <ReviewForm
          assessment={detailedAssessment}
          onSuccess={onReviewSuccess}
          onCancel={() => setShowReviewForm(false)}
        />
      ) : (
        <>
          <Stack vertical gap={16}>
            <Heading>Basic Information</Heading>
            <Stack vertical gap={8}>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  External ID
                </Small>
                <Small>{detailedAssessment.external_id}</Small>
              </Stack>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  User ID
                </Small>
                <Small>{detailedAssessment.user_id}</Small>
              </Stack>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  Skill ID
                </Small>
                <Small>{detailedAssessment.skill_id}</Small>
              </Stack>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  Status
                </Small>
                {detailedAssessment.result_status ? (
                  <Chip
                    color={getResultStatusColor(
                      detailedAssessment.result_status,
                    )}
                    label={detailedAssessment.result_status.replace(/_/g, ' ')}
                  />
                ) : (
                  <Small>N/A</Small>
                )}
              </Stack>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  Language
                </Small>
                <Small>{detailedAssessment.language.toUpperCase()}</Small>
              </Stack>
              <Stack justify="apart">
                <Small color="light" weight="semibold">
                  Created At
                </Small>
                <Small>{formatDate(detailedAssessment.created_at)}</Small>
              </Stack>
            </Stack>
          </Stack>

          {detailedAssessment.reviewed_at && (
            <Stack vertical gap={16}>
              <Heading>Review Information</Heading>
              <Stack vertical gap={8}>
                <Stack justify="apart">
                  <Small color="light" weight="semibold">
                    Final Status
                  </Small>
                  <Chip
                    color="primary"
                    label={detailedAssessment.final_status || 'N/A'}
                  />
                </Stack>
                <Stack justify="apart">
                  <Small color="light" weight="semibold">
                    Final Score
                  </Small>
                  <Small>{detailedAssessment.final_score || 'N/A'}</Small>
                </Stack>
                <Stack justify="apart">
                  <Small color="light" weight="semibold">
                    Reviewed By
                  </Small>
                  <Small>{detailedAssessment.reviewed_by_id || 'N/A'}</Small>
                </Stack>
                <Stack justify="apart">
                  <Small color="light" weight="semibold">
                    Reviewed At
                  </Small>
                  <Small>{formatDate(detailedAssessment.reviewed_at)}</Small>
                </Stack>
                {detailedAssessment.notes && (
                  <Stack vertical gap={4}>
                    <Small color="light" weight="semibold">
                      Notes
                    </Small>
                    <Body>{detailedAssessment.notes}</Body>
                  </Stack>
                )}
              </Stack>
            </Stack>
          )}

          {detailedAssessment.questions &&
            detailedAssessment.questions.length > 0 && (
              <Stack vertical gap={16}>
                <Heading>
                  Questions & Answers ({detailedAssessment.questions.length})
                </Heading>
                <Stack vertical gap={16}>
                  {detailedAssessment.questions.map((question, index) => (
                    <div
                      key={question.id}
                      style={{
                        backgroundColor: '#f9f9f9',
                        padding: '16px',
                        borderRadius: '8px',
                        width: '100%',
                      }}
                    >
                      <Stack vertical gap={16}>
                        <Stack justify="apart" align="center">
                          <Chip
                            color="primary"
                            label={`Question ${index + 1}`}
                          />
                          {question.answer && (
                            <Small weight="semibold">
                              Score: {question.answer.score}
                            </Small>
                          )}
                        </Stack>

                        <Body>{question.question_text}</Body>

                        {question.audio_s3_key && (
                          <Stack vertical gap={4}>
                            <Small weight="semibold">Question Audio:</Small>
                            <audio controls style={{ width: '100%' }}>
                              <source
                                src={question.audio_s3_key}
                                type="audio/mpeg"
                              />
                              Your browser does not support the audio element.
                            </audio>
                          </Stack>
                        )}

                        {question.answer ? (
                          <>
                            <Stack vertical gap={4}>
                              <Small weight="semibold">
                                Answer Transcript:
                              </Small>
                              <div
                                style={{
                                  backgroundColor: '#f0f7ff',
                                  padding: '12px 16px',
                                  borderRadius: '6px',
                                  borderLeft: '3px solid #4a90e2',
                                  color: '#2c3e50',
                                }}
                              >
                                <Body>{question.answer.transcript}</Body>
                              </div>
                            </Stack>
                            {question.answer.recording_url && (
                              <Stack vertical gap={4}>
                                <Small weight="semibold">
                                  Answer Recording:
                                </Small>
                                <audio controls style={{ width: '100%' }}>
                                  <source
                                    src={question.answer.recording_url}
                                    type="audio/mpeg"
                                  />
                                  Your browser does not support the audio
                                  element.
                                </audio>
                              </Stack>
                            )}
                            {question.answer.ai_feedback && (
                              <Stack vertical gap={4}>
                                <Small weight="semibold">AI Feedback:</Small>
                                <div
                                  style={{
                                    backgroundColor: '#f0f7ff',
                                    padding: '12px 16px',
                                    borderRadius: '6px',
                                    borderLeft: '3px solid #4a90e2',
                                    color: '#2c3e50',
                                  }}
                                >
                                  <Body>
                                    {typeof question.answer.ai_feedback ===
                                    'string'
                                      ? question.answer.ai_feedback
                                      : JSON.stringify(
                                          question.answer.ai_feedback,
                                          null,
                                          2,
                                        )}
                                  </Body>
                                </div>
                              </Stack>
                            )}
                          </>
                        ) : (
                          <Body style={{ fontStyle: 'italic' }}>
                            No answer provided
                          </Body>
                        )}

                        {question.category && (
                          <Chip color="success" label={question.category} />
                        )}
                      </Stack>
                    </div>
                  ))}
                </Stack>
              </Stack>
            )}
        </>
      )}
    </Stack>
  );
};

export default AssessmentDetail;
